import { Button } from 'slab'
import { type MoneyAmount } from 'types'
import { getLocaleCurrencyStringFromNumber, getMinifiedLocaleCurrencyStringFromMoneyAmount } from 'utils'

import { OrderProductCard } from '../shared/OrderProductCard'
import { type MarketplaceProduct, type MarketplaceProductVariant } from '../shared/types'

type OrderSummaryProps = {
  balance: MoneyAmount;
  product: MarketplaceProduct;
  selectedVariant: MarketplaceProductVariant;
  onBuyNow: () => void;
  isProcessing?: boolean;
  isDisabled?: boolean;
  isMobileView?: boolean;
}

export const OrderSummary = ({
  balance,
  product,
  selectedVariant,
  onBuyNow,
  isProcessing = false,
  isDisabled = false,
  isMobileView = false,
}: OrderSummaryProps) => {
  const productPrice = selectedVariant.discounted_price ?? selectedVariant.base_price
  const productPriceCents = productPrice ? parseFloat(productPrice.value) * 100 : 0

  const balanceCents = balance ? parseFloat(balance.value) * 100 : 0
  const remainingBalanceCents = balanceCents - productPriceCents

  return (
    <div className={isMobileView ? 'flex flex-col' : 'flex flex-col h-full'}>
      <div className={isMobileView ? 'flex flex-col gap-4 mt-4' : 'flex flex-col mt-24 gap-6 px-6'}>
        {balance && (
          <div>
            <p className='text-sm text-gray-500'>Thatch balance</p>
            <p className={`${isMobileView ? 'heading-sm' : 'heading-xl'} font-normal text-green-400`}>
              {getMinifiedLocaleCurrencyStringFromMoneyAmount(balance)}
            </p>
          </div>
        )}

        <OrderProductCard
          isMobileView={isMobileView}
          productBrand={product.product_brand}
          productSlug={product.slug}
          variantName={selectedVariant.name}
          variantBasePrice={selectedVariant.base_price}
          variantDiscountedPrice={selectedVariant.discounted_price}
          variantThumbnailImageUrl={selectedVariant.thumbnail_image_urls[0]}
        />

        <div className='flex-1 mt-4'>
          <div className='space-y-3'>
            <div className='flex justify-between items-center'>
              <span className='body-bold'>Total</span>
              <span>
                {getLocaleCurrencyStringFromNumber(productPriceCents / 100, { maximumFractionDigits: 2 })}
              </span>
            </div>

            {balance && (
              <div className='flex justify-between items-center'>
                <span className='body-bold'>Remaining balance</span>
                <span className='body-bold text-green-400'>
                  {getLocaleCurrencyStringFromNumber(remainingBalanceCents / 100, { maximumFractionDigits: 2 })}
                </span>
              </div>
            )}
          </div>
        </div>

        <Button
          onClick={onBuyNow}
          disabled={!!isDisabled || isProcessing || (!!balance && remainingBalanceCents < 0)}
          size='large'
          variant='primary'
          width='full'
        >
          {isProcessing ? 'Processing...' : 'Buy now'}
        </Button>
      </div>
    </div>
  )
}
